import { FishingReport, WaterbodyPin } from '@omniafishing/core';
import classNames from 'classnames';
import _, { noop } from 'lodash';
import React from 'react';
import { useHistory } from 'react-router';
import { useDimensions } from '../../hooks/use_dimensions';
import { useGeographicLocation } from '../../hooks/use_geographic_location';
import { useResponsive } from '../../hooks/use_responsive';
import { useUser } from '../../hooks/use_user';
import { apiV1 } from '../../lib/api';
import { setShopifyImgWidth } from '../../lib/set_shopify_img_width';
import { OmniaUrls } from '../../lib/urls';
import { WebAnalytics } from '../../lib/web_analytics';
import { getPinLimit } from '../../routes/map_page/lib/map_page_helpers';
import { LoadableMap } from '../map/loadable_omnia_map';
import { BASE_LAYER, LAYER_TYPES, OVERLAY_LAYER } from '../map/map_types';
import { MapBounds } from '../map/omnia_map';
import { MOBILE_PINS_LIMIT } from '../map_default/map_default';
import { OmniaLinkButton } from '../omnia_button/omnia_link_button';

export const UserMap = () => {
  const { user } = useUser();
  const { first_name, display_name } = user;
  const name = first_name || display_name;

  const history = useHistory();
  const { isMobile } = useResponsive();
  const { ipLatitude, ipLongitude } = useGeographicLocation();
  const viewport = {
    longitude: ipLongitude,
    latitude: ipLatitude,
    zoom: 10,
  };
  const mapUrl =
    OmniaUrls.map({
      lat: viewport.latitude,
      lng: viewport.longitude,
      zoom: viewport.zoom,
    }) + '&initial_modal=hotbaits';
  const [measureRef, measureBounds] = useDimensions({
    onResize: true,
    onElementResize: true,
  });
  const [markers, setMarkers] = React.useState<WaterbodyPin[]>([]);
  const [hotbaitReports, setHotbaitReports] = React.useState<FishingReport[]>([]);

  const fetchLakes = (bounds: MapBounds, zoomParam: number) => {
    if (bounds == null) {
      return;
    }

    if (bounds.e === 0 && bounds.n === 0 && bounds.s === 0 && bounds.w === 0) {
      return;
    }

    const desktopPinsToFetch = getPinLimit(Number(zoomParam));
    const fetchParams: Parameters<typeof apiV1.mapPinsFetch>[0] = {
      ...bounds,
      limit: isMobile ? MOBILE_PINS_LIMIT : desktopPinsToFetch,
    };

    apiV1
      .mapPinsFetch(fetchParams)
      .then((response) => {
        setMarkers(response.data.data);
      })
      .catch((error) => {
        console.error(`ERROR: fetchPins`, error);
      });
  };

  const fetchHotbaits = (bounds: MapBounds) => {
    apiV1
      .mapFishingReportsFetch({
        ...bounds,
        per_page: 2,
      })
      .then((response) => {
        setHotbaitReports(response.data.data);
      })
      .catch((error) => {
        console.error(`ERROR: fetchHotbaits`, error);
      });
  };

  const onViewportChange = (thisViewport: any, bounds: MapBounds) => {
    fetchLakes(bounds, thisViewport.zoom);
    fetchHotbaits(bounds);
  };
  const onViewportChangeDebounced = React.useCallback(
    _.debounce((thisViewport: any, bounds: MapBounds) => {
      onViewportChange(thisViewport, bounds);
    }, 300),
    []
  );

  if (!ipLatitude || !ipLongitude) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6 sm:flex-row p-6 sm:pt-12 isolate">
      <div className="sm:w-1/3 flex flex-col gap-3 justify-center">
        {name && <p className="font-termina font-bold text-[#FF2A00]">Hi {name}</p>}
        <p className="font-termina text-[32px] font-bold text-[#061622] leading-[1.15]">
          Shop by Lake.
          <br />
          Fish What’s Working.
        </p>
        <p className="text-xl text-[#6B7172]">
          See trending tackle and active lakes in your region. Tap the map to explore baits proven
          to work.
        </p>
      </div>
      <div className="sm:w-2/3">
        <div className="h-[400px] relative select-none rounded-xl overflow-hidden" ref={measureRef}>
          {measureBounds.width != null && (
            <>
              <LoadableMap
                height={measureBounds.height}
                loadInteractiveImages
                mapLayerState={{
                  [LAYER_TYPES.BASE]: BASE_LAYER.STANDARD,
                  [LAYER_TYPES.OVERLAY]: [OVERLAY_LAYER.PINS],
                  [LAYER_TYPES.CORE]: null,
                  [LAYER_TYPES.WEATHER]: [],
                  [LAYER_TYPES.POINTS_OF_INTEREST]: [],
                }}
                markers={markers}
                navControl={false}
                onMapClick={noop}
                onLoad={(bounds, _mapRef) => {
                  fetchLakes(bounds, viewport.zoom);
                  fetchHotbaits(bounds);
                }}
                onPinClick={(waterbody) => {
                  history.push(
                    OmniaUrls.map({
                      lat: waterbody.lat,
                      lng: waterbody.lng,
                      zoom: 10,
                      waterbodySlug: waterbody.url_slug,
                    })
                  );
                  WebAnalytics.homepageLoggedInMapPinClick(waterbody.url_slug);
                }}
                onViewportChange={onViewportChangeDebounced}
                viewport={viewport}
                waypoints={[]}
                width={measureBounds.width}
                disableScrollZoom
              />
              <OmniaLinkButton
                size="lg"
                kind="tertiary"
                to={mapUrl}
                className={classNames('absolute bottom-3 left-3 z-[500]', {
                  'w-[calc(100%-24px)]': isMobile,
                })}
                onClick={() => {
                  WebAnalytics.homepageLoggedInMapButtonClick(mapUrl);
                }}
              >
                Expand Map
              </OmniaLinkButton>
              {hotbaitReports.length > 0 && (
                <div className="absolute top-3 right-3 z-[500] bg-[#061622C7] p-6 rounded-lg shadow-lg text-white max-w-[220px] max-h-[325px]">
                  <p className="font-termina text-[13px] font-bold mb-2">Trending Tackle</p>
                  <div className="flex gap-3">
                    {hotbaitReports.map((report) => (
                      <div key={report.id} className="w-1/2 flex flex-col gap-2 text-xs">
                        <img
                          src={setShopifyImgWidth(report.featured_product.img_url, 200)}
                          alt={report.featured_product.title_short}
                          width={82}
                          className="rounded-lg"
                        />
                        <p>{report.featured_product.title}</p>
                        <p className="text-[11px] opacity-55">{report.waterbody.primary_name}</p>
                      </div>
                    ))}
                  </div>
                  <OmniaLinkButton
                    size="md"
                    kind="primary"
                    to={mapUrl}
                    className="mt-2"
                    block
                    onClick={() => {
                      WebAnalytics.homepageLoggedInMapButtonClick(mapUrl);
                    }}
                  >
                    See Trending Tackle →
                  </OmniaLinkButton>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
